import {
  buildChatEndpoint,
  buildLeadCaptureBody,
  buildLeadEndpoint,
  buildOrderStatusEndpoint,
  buildProductJsonEndpoint,
  buildPredictiveSearchEndpoint,
  buildRelatedRecommendationsEndpoint,
  buildRequestBody,
  deriveSearchQuery,
  isOrderQuery,
  isSizingQuestion,
} from './ezra-chat-client.js';

function escapeHtml(value) {
  return `${value || ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getContext(root) {
  const contextNode = root.querySelector('[data-ezra-context]');

  if (!contextNode) {
    return {
      pageType: 'unknown',
      pageTitle: document.title,
      productTitle: null,
      productHandle: null,
      productId: null,
      productDescriptionHtml: null,
    };
  }

  try {
    return JSON.parse(contextNode.textContent);
  } catch (error) {
    console.error('Ezra context parse error', error);
    return {
      pageType: 'unknown',
      pageTitle: document.title,
      productTitle: null,
      productHandle: null,
      productId: null,
      productDescriptionHtml: null,
    };
  }
}

function renderMessage(container, message) {
  const item = document.createElement('div');
  item.className = `ezra-chat__message ezra-chat__message--${message.role}`;
  item.innerHTML = `
    <div class="ezra-chat__bubble">
      <p>${escapeHtml(message.content)}</p>
    </div>
  `;
  container.appendChild(item);
  container.scrollTop = container.scrollHeight;
}

function renderProductCards(container, products = []) {
  const visible = products
    .filter((p) => p && (p.title || p.handle))
    .slice(0, 4);

  if (visible.length === 0) return;

  const row = document.createElement('div');
  row.className = 'ezra-chat__product-row';

  for (const product of visible) {
    const url = product.url || `/products/${product.handle}`;
    const rawPrice = Number(product.price);
    const price = rawPrice
      ? rawPrice > 500 ? `$${(rawPrice / 100).toFixed(2)}` : `$${rawPrice.toFixed(2)}`
      : '';
    const imgSrc = product.image || product.featured_image || '';

    const card = document.createElement('a');
    card.href = url;
    card.className = 'ezra-chat__product-card';
    card.innerHTML = [
      imgSrc
        ? `<img class="ezra-chat__product-card-img" src="${escapeHtml(imgSrc)}" alt="${escapeHtml(product.title)}" loading="lazy">`
        : '<div class="ezra-chat__product-card-img ezra-chat__product-card-img--empty"></div>',
      `<span class="ezra-chat__product-card-title">${escapeHtml(product.title)}</span>`,
      price ? `<span class="ezra-chat__product-card-price">${escapeHtml(price)}</span>` : '',
      '<span class="ezra-chat__product-card-link">View</span>',
    ].join('');

    row.appendChild(card);
  }

  container.appendChild(row);
  setTimeout(() => { container.scrollTop = container.scrollHeight; }, 50);
}

function setStatus(root, text) {
  const status = root.querySelector('[data-ezra-status]');
  if (status) status.textContent = text;
}

function normalizeLiveProducts(products = []) {
  return products
    .filter((product) => product && (product.title || product.handle))
    .slice(0, 6)
    .map((product) => ({
      id: product.id || null,
      title: product.title || '',
      handle: product.handle || '',
      url: product.url || (product.handle ? `/products/${product.handle}` : ''),
      price:
        product.price ||
        product.price_min ||
        product.price_max ||
        product.price_min_amount ||
        null,
      image:
        product.featured_image?.url ||
        product.featured_image ||
        product.image ||
        product.images?.[0]?.src ||
        product.images?.[0] ||
        null,
      vendor: product.vendor || '',
      productType: product.type || product.product_type || '',
      productDescriptionHtml: product.body_html || product.description_html || null,
    }));
}

async function enrichProductsForSizing(products = []) {
  const candidates = products.slice(0, 4);

  const enriched = await Promise.all(
    candidates.map(async (product) => {
      if (!product?.handle || product.productDescriptionHtml) {
        return product;
      }

      try {
        const response = await fetch(buildProductJsonEndpoint(product.handle), {
          headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
          return product;
        }

        const data = await response.json();
        return {
          ...product,
          productType: product.productType || data.product_type || '',
          productDescriptionHtml: data.body_html || null,
        };
      } catch (error) {
        console.error('Ezra product json fetch error', error);
        return product;
      }
    })
  );

  return [
    ...enriched,
    ...products.slice(candidates.length),
  ];
}

async function fetchLiveProducts(message, context) {
  const candidates = [];

  if (context.productId) {
    try {
      const relatedResponse = await fetch(buildRelatedRecommendationsEndpoint(context.productId, 4), {
        headers: { Accept: 'application/json' },
      });

      if (relatedResponse.ok) {
        const relatedData = await relatedResponse.json();
        candidates.push(...normalizeLiveProducts(relatedData.products || []));
      }
    } catch (error) {
      console.error('Ezra related recommendations error', error);
    }
  }

  const query = deriveSearchQuery(message, context);

  if (query) {
    try {
      const searchResponse = await fetch(buildPredictiveSearchEndpoint(query, 4), {
        headers: { Accept: 'application/json' },
      });

      if (searchResponse.ok) {
        const searchData = await searchResponse.json();
        const products =
          searchData.resources?.results?.products ||
          searchData.resources?.products ||
          [];

        candidates.push(...normalizeLiveProducts(products));
      }
    } catch (error) {
      console.error('Ezra predictive search error', error);
    }
  }

  const deduped = [];
  const seen = new Set();

  for (const product of candidates) {
    const key = product.handle || product.title;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    deduped.push(product);
  }

  const limited = deduped.slice(0, 6);

  if (!isSizingQuestion(message)) {
    return limited;
  }

  return enrichProductsForSizing(limited);
}

const CONVERSATION_KEY = 'ezra_conversation';
const CONVERSATION_MAX = 30;

function saveConversation(messages) {
  try {
    const trimmed = messages.slice(-CONVERSATION_MAX);
    sessionStorage.setItem(CONVERSATION_KEY, JSON.stringify(trimmed));
  } catch { /* storage full or private browsing */ }
}

function loadConversation() {
  try {
    const raw = sessionStorage.getItem(CONVERSATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function clearConversation() {
  try {
    sessionStorage.removeItem(CONVERSATION_KEY);
  } catch { /* private browsing */ }
}

const BROWSE_HISTORY_KEY = 'ezra_browse_history';
const BROWSE_HISTORY_MAX = 20;

function saveBrowseHistory(context) {
  if (context.pageType !== 'product' || !context.productHandle) return;
  try {
    const raw = localStorage.getItem(BROWSE_HISTORY_KEY);
    const history = raw ? JSON.parse(raw) : [];
    const existing = history.findIndex((h) => h.handle === context.productHandle);
    if (existing !== -1) history.splice(existing, 1);
    history.unshift({
      handle: context.productHandle,
      title: context.productTitle || '',
      type: context.productType || '',
      price: context.productPrice || '',
      ts: Date.now(),
    });
    localStorage.setItem(BROWSE_HISTORY_KEY, JSON.stringify(history.slice(0, BROWSE_HISTORY_MAX)));
  } catch { /* storage full */ }
}

function getBrowseHistory() {
  try {
    const raw = localStorage.getItem(BROWSE_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

const LAST_VISIT_KEY = 'ezra_last_visit';
const RETURN_THRESHOLD_MS = 30 * 60 * 1000;

function getReturnVisitProducts() {
  try {
    const lastVisit = Number(localStorage.getItem(LAST_VISIT_KEY)) || 0;
    const now = Date.now();
    localStorage.setItem(LAST_VISIT_KEY, `${now}`);

    if (!lastVisit || (now - lastVisit) < RETURN_THRESHOLD_MS) return [];

    const history = getBrowseHistory();
    return history
      .filter((item) => item.ts && item.ts <= lastVisit)
      .slice(0, 3);
  } catch { return []; }
}

const WISHLIST_KEY = 'ezra_wishlist';
const WISHLIST_MAX = 30;

function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function addToWishlist(product) {
  const list = getWishlist();
  if (list.some((p) => p.handle === product.handle)) return false;
  list.unshift({
    handle: product.handle,
    title: product.title || '',
    price: product.price || '',
    url: product.url || `/products/${product.handle}`,
  });
  try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list.slice(0, WISHLIST_MAX))); } catch {}
  return true;
}

function removeFromWishlist(handle) {
  const list = getWishlist();
  const filtered = list.filter((p) => p.handle !== handle);
  try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(filtered)); } catch {}
  return filtered.length < list.length;
}

function isWishlistTrigger(msg) {
  const normalized = msg.toLowerCase().trim();
  return /\b(wishlist|saved|save for later|my list|saved items)\b/.test(normalized);
}

function isSaveProductTrigger(msg) {
  const normalized = msg.toLowerCase().trim();
  return /\b(save this|save it|add to wishlist|save for later|remember this)\b/.test(normalized);
}

function isReferralTrigger(msg) {
  return /\b(referral|refer.*friend|get my referral code|referral code)\b/i.test(msg);
}

async function fetchReferralCode(workerUrl, email, name) {
  try {
    const base = `${workerUrl || ''}`.trim().replace(/\/+$/, '');
    if (!base) return null;
    const res = await fetch(`${base}/api/referral`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'create', email, name }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

function isShippingQuery(msg) {
  return /\b(shipping|ship to|delivery|deliver to|shipping cost|shipping rate|how much.*ship)\b/i.test(msg);
}

function looksLikeZipCode(msg) {
  const trimmed = msg.trim();
  return /^\d{5}(-\d{4})?$/.test(trimmed);
}

async function fetchShippingEstimate(workerUrl, zip, countryCode = 'US') {
  try {
    const base = `${workerUrl || ''}`.trim().replace(/\/+$/, '');
    if (!base) return null;
    const res = await fetch(`${base}/api/shipping-estimate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ zip, countryCode }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.ok ? data : null;
  } catch { return null; }
}

const OFFER_CLAIMED_KEY = 'ezra_offer_claimed';
const CUSTOMER_NAME_KEY = 'ezra_customer_name';

function isOfferClaimed() {
  try {
    return localStorage.getItem(OFFER_CLAIMED_KEY) === '1';
  } catch {
    return false;
  }
}

function markOfferClaimed() {
  try {
    localStorage.setItem(OFFER_CLAIMED_KEY, '1');
  } catch {
    /* private browsing */
  }
}

function saveCustomerName(name) {
  try {
    const trimmed = `${name || ''}`.trim();
    if (trimmed) localStorage.setItem(CUSTOMER_NAME_KEY, trimmed);
  } catch { /* private browsing */ }
}

function getCustomerName() {
  try {
    return localStorage.getItem(CUSTOMER_NAME_KEY) || '';
  } catch {
    return '';
  }
}

function showThankYou(root) {
  const offerCard = root.querySelector('[data-ezra-offer]');
  const leadCapture = root.querySelector('[data-ezra-lead]');

  if (leadCapture) leadCapture.hidden = true;

  if (offerCard) {
    const eyebrow = offerCard.querySelector('.ezra-chat__offer-eyebrow');
    const title = offerCard.querySelector('.ezra-chat__offer-title');
    const copy = offerCard.querySelector('.ezra-chat__offer-copy');
    const button = offerCard.querySelector('.ezra-chat__offer-button');

    if (eyebrow) eyebrow.textContent = 'Welcome Offer Saved';
    if (title) title.textContent = 'Your code: Blessing10%';
    if (copy) copy.textContent = 'Use it at checkout for 10% off your first order. Check your inbox too.';
    if (button) button.hidden = true;

    offerCard.hidden = false;
  }
}

function toggleLeadCapture(root, shouldShow, summary = '', mode = 'support') {
  const capture = root.querySelector('[data-ezra-lead]');
  const summaryInput = root.querySelector('[data-ezra-lead-summary]');
  const summaryWrap = root.querySelector('[data-ezra-lead-summary-wrap]');
  const leadTitle = root.querySelector('.ezra-chat__lead-title');
  const leadCopy = root.querySelector('.ezra-chat__lead-copy');
  const leadSubmit = root.querySelector('.ezra-chat__lead-submit');
  const leadTypeInput = root.querySelector('[data-ezra-lead-type]');
  const leadOfferInput = root.querySelector('[data-ezra-lead-offer]');
  const offerCard = root.querySelector('[data-ezra-offer]');
  const offerPercentage = Number.parseInt(root.dataset.discountPercentage || '10', 10) || 10;

  if (!capture) return;

  capture.hidden = !shouldShow;
  if (offerCard) {
    offerCard.hidden = shouldShow;
  }

  if (leadTitle && leadCopy && leadSubmit && leadTypeInput && leadOfferInput) {
    if (mode === 'discount') {
      leadTitle.textContent = `Get ${offerPercentage}% off your first order`;
      leadCopy.textContent =
        'Share your email and Ezra will save your welcome offer and future new-drop updates.';
      leadSubmit.textContent = `Get ${offerPercentage}% off`;
      leadTypeInput.value = 'discount-offer';
      leadOfferInput.value = `${offerPercentage}`;
      if (summaryWrap) summaryWrap.hidden = true;
      if (summaryInput) {
        summaryInput.required = false;
        summaryInput.value = `${offerPercentage}% welcome offer signup`;
      }
      return;
    }

    leadTitle.textContent = 'Need a human follow-up?';
    leadCopy.textContent = 'Leave your details and a short note, and we will follow up with care.';
    leadSubmit.textContent = 'Request follow-up';
    leadTypeInput.value = 'support-follow-up';
    leadOfferInput.value = '';
    if (summaryWrap) summaryWrap.hidden = false;
    if (summaryInput) {
      summaryInput.required = true;
    }
  }

  if (summaryInput && summary && mode === 'support') {
    summaryInput.value = summary;
  }
}

async function requestEzraReply(root, state, context) {
  const workerUrl = root.dataset.workerUrl || '';
  const endpoint = buildChatEndpoint(workerUrl);
  const latestUserMessage = state.messages[state.messages.length - 1]?.content || '';
  const liveProducts = await fetchLiveProducts(latestUserMessage, context);

  if (!endpoint) {
    return {
      reply:
        'Peace to you. I am Ezra, and I am almost ready. My live service endpoint has not been connected yet, but I will soon be able to help with products, sizing, shipping, and store questions.',
      escalationSuggested: false,
      liveProducts,
    };
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      buildRequestBody({
        messages: state.messages,
        context: {
          ...context,
          liveProducts,
        },
      })
    ),
  });

  if (!response.ok) {
    throw new Error(`Ezra request failed with status ${response.status}`);
  }

  const data = await response.json();
  data.liveProducts = liveProducts;
  return data;
}

async function submitLeadCapture(root) {
  const workerUrl = root.dataset.workerUrl || '';
  const endpoint = buildLeadEndpoint(workerUrl);
  const name = root.querySelector('[data-ezra-lead-name]')?.value?.trim() || '';
  const email = root.querySelector('[data-ezra-lead-email]')?.value?.trim() || '';
  const summary = root.querySelector('[data-ezra-lead-summary]')?.value?.trim() || '';
  const leadType = root.querySelector('[data-ezra-lead-type]')?.value || 'support-follow-up';
  const offerPercentage = Number.parseInt(
    root.querySelector('[data-ezra-lead-offer]')?.value || '',
    10
  );

  if (!endpoint) {
    setStatus(root, 'Please email us directly and we will follow up.');
    return;
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    setStatus(root, 'Please enter a valid email address.');
    return;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(
      buildLeadCaptureBody({
        name,
        email,
        summary,
        leadType,
        offerPercentage: Number.isNaN(offerPercentage) ? null : offerPercentage,
      })
    ),
  });

  if (!response.ok) {
    throw new Error(`Lead capture failed with status ${response.status}`);
  }

  const payload = await response.json();

  const submittedName = root.querySelector('[data-ezra-lead-name]')?.value?.trim() || '';
  if (submittedName) saveCustomerName(submittedName);
  if (email) { try { localStorage.setItem('ezra_lead_email', email); } catch {} }

  if (leadType === 'discount-offer') {
    markOfferClaimed();
    showThankYou(root);
    setStatus(
      root,
      payload.delivery === 'omnisend'
        ? 'You are in. Watch your inbox for your welcome offer.'
        : 'Thanks. Ezra received your email for the welcome offer.'
    );
  } else {
    setStatus(root, 'Thank you. We received your note and will follow up.');
  }
}

function showOrderLookup(root, show) {
  const orderSection = root.querySelector('[data-ezra-order-lookup]');
  const chatForm = root.querySelector('[data-ezra-form]');
  if (orderSection) orderSection.hidden = !show;
  if (chatForm) chatForm.style.display = show ? 'none' : '';
}

async function submitOrderLookup(root) {
  const workerUrl = root.dataset.workerUrl || '';
  const endpoint = buildOrderStatusEndpoint(workerUrl);
  const emailInput = root.querySelector('[data-ezra-order-email]');
  const numberInput = root.querySelector('[data-ezra-order-number]');
  const email = emailInput?.value?.trim() || '';
  const orderNumber = numberInput?.value?.trim() || '';

  if (!endpoint) {
    setStatus(root, 'Order lookup is not available right now.');
    return;
  }

  if (!email || !orderNumber) {
    setStatus(root, 'Please enter both your email and order number.');
    return;
  }

  setStatus(root, 'Looking up your order...');

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, orderNumber }),
    });

    const data = await response.json();
    const orderForm = root.querySelector('[data-ezra-order-form]');

    const existingResult = root.querySelector('.ezra-chat__order-result');
    if (existingResult) existingResult.remove();

    const resultDiv = document.createElement('div');
    resultDiv.className = 'ezra-chat__order-result';

    if (data.found && data.summary) {
      resultDiv.innerHTML = data.summary
        .map((line) => {
          if (line.startsWith('Track your package:')) {
            const url = line.replace('Track your package: ', '');
            return `<p><a href="${escapeHtml(url)}" target="_blank" rel="noopener">Track your package</a></p>`;
          }
          return `<p>${escapeHtml(line)}</p>`;
        })
        .join('');
      setStatus(root, 'Order found.');
    } else {
      resultDiv.textContent = data.message || 'Could not find that order. Please double-check and try again.';
      setStatus(root, 'Order not found.');
    }

    orderForm?.insertAdjacentElement('afterend', resultDiv);
  } catch (error) {
    console.error('Order lookup error', error);
    setStatus(root, 'Something went wrong. Please try again.');
  }
}

async function fetchDailyVerse(workerUrl) {
  try {
    const base = `${workerUrl || ''}`.trim().replace(/\/+$/, '');
    if (!base) return null;
    const res = await fetch(`${base}/api/daily-verse`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.ok ? data : null;
  } catch { return null; }
}

function initEzra(root) {
  const panel = root.querySelector('[data-ezra-panel]');
  const launcher = root.querySelector('[data-ezra-launcher]');
  const closeButton = root.querySelector('[data-ezra-close]');
  const form = root.querySelector('[data-ezra-form]');
  const input = root.querySelector('[data-ezra-input]');
  const feed = root.querySelector('[data-ezra-feed]');
  const leadForm = root.querySelector('[data-ezra-lead-form]');
  const offerButton = root.querySelector('[data-ezra-offer-button]');
  const orderForm = root.querySelector('[data-ezra-order-form]');
  const orderBackBtn = root.querySelector('[data-ezra-order-back]');
  const newChatBtn = root.querySelector('[data-ezra-new-chat]');
  const context = getContext(root);

  saveBrowseHistory(context);
  context.browseHistory = getBrowseHistory();

  const customerName = getCustomerName();
  const baseWelcome = root.dataset.welcome || 'Peace to you. I am Ezra. How can I help today?';
  const personalizedWelcome = customerName
    ? `Welcome back, ${customerName}! Peace to you. How can I help today?`
    : baseWelcome;

  const welcomeMessage = {
    role: 'assistant',
    content: personalizedWelcome,
  };

  const saved = loadConversation();
  const state = {
    messages: saved || [welcomeMessage],
  };

  for (const msg of state.messages) {
    renderMessage(feed, msg);
  }

  const returnProducts = !saved ? getReturnVisitProducts() : [];
  let enrichmentStarted = false;

  function enrichConversation() {
    if (enrichmentStarted || saved) return;
    enrichmentStarted = true;

    fetchDailyVerse(root.dataset.workerUrl).then((verse) => {
      if (!verse) return;
      const verseMsg = {
        role: 'assistant',
        content: `Today's Scripture: "${verse.text}" — ${verse.ref}`,
      };
      state.messages.push(verseMsg);
      renderMessage(feed, verseMsg);
      saveConversation(state.messages);

      if (returnProducts.length > 0) {
        const names = returnProducts.map((p) => p.title || p.handle).join(', ');
        const recoveryMsg = {
          role: 'assistant',
          content: returnProducts.length === 1
            ? `Welcome back! Last time you were checking out the ${names}. Still interested?`
            : `Welcome back! Last time you were looking at ${names}. Want to pick up where you left off?`,
        };
        state.messages.push(recoveryMsg);
        renderMessage(feed, recoveryMsg);
        saveConversation(state.messages);
        const cards = returnProducts.map((p) => ({
          title: p.title || p.handle,
          handle: p.handle,
          price: p.price || null,
          url: `/products/${p.handle}`,
        }));
        renderProductCards(feed, cards);
      }
    });
  }

  if (isOfferClaimed()) {
    showThankYou(root);
  }

  function openPanel() {
    panel.hidden = false;
    launcher.hidden = true;
    panel.style.display = '';
    launcher.style.display = 'none';
    enrichConversation();
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouchDevice && input) {
      requestAnimationFrame(() => input.focus());
    }
  }

  function closePanel() {
    panel.hidden = true;
    launcher.hidden = false;
    panel.style.display = 'none';
    launcher.style.display = '';
  }

  launcher?.addEventListener('click', openPanel);

  closeButton?.addEventListener('click', closePanel);

  offerButton?.addEventListener('click', () => {
    toggleLeadCapture(root, true, '', 'discount');
  });

  newChatBtn?.addEventListener('click', () => {
    clearConversation();
    state.messages = [welcomeMessage];
    feed.innerHTML = '';
    renderMessage(feed, welcomeMessage);
    showOrderLookup(root, false);
    toggleLeadCapture(root, false);
    setStatus(root, 'Ezra is ready.');
  });

  orderForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    await submitOrderLookup(root);
  });

  orderBackBtn?.addEventListener('click', () => {
    showOrderLookup(root, false);
    setStatus(root, 'Ezra is ready.');
  });

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();

    const message = input?.value?.trim();
    if (!message) return;

    const userMessage = { role: 'user', content: message };
    state.messages.push(userMessage);
    renderMessage(feed, userMessage);
    saveConversation(state.messages);
    input.value = '';
    setStatus(root, 'Ezra is thinking...');

    if (isOrderQuery(message)) {
      const orderPrompt = {
        role: 'assistant',
        content: 'I can help you check on your order. Please use the form below — just enter the email you ordered with and your order number.',
      };
      state.messages.push(orderPrompt);
      renderMessage(feed, orderPrompt);
      saveConversation(state.messages);
      showOrderLookup(root, true);
      setStatus(root, 'Enter your order details below.');
      return;
    }

    if (isReferralTrigger(message) && isOfferClaimed()) {
      const email = localStorage.getItem('ezra_lead_email') || '';
      const name = getCustomerName() || '';
      if (email) {
        const typingMsg = { role: 'assistant', content: 'Getting your referral code…' };
        state.messages.push(typingMsg);
        renderMessage(feed, typingMsg);
        const result = await fetchReferralCode(root.dataset.workerUrl, email, name);
        state.messages.pop();
        feed.lastElementChild?.remove();
        if (result && result.ok) {
          const reply = {
            role: 'assistant',
            content: result.alreadyExisted
              ? `Your referral code is **${result.code}**. Share it with friends — they will get 10% off their first order.`
              : `Here is your brand-new referral code: **${result.code}**. Share it with friends and they will get 10% off their first order.`,
          };
          state.messages.push(reply);
          renderMessage(feed, reply);
          saveConversation(state.messages);
        } else {
          const reply = { role: 'assistant', content: 'I was not able to generate a referral code right now. Please try again in a moment.' };
          state.messages.push(reply);
          renderMessage(feed, reply);
          saveConversation(state.messages);
        }
      } else {
        const reply = { role: 'assistant', content: 'I would love to set up your referral code. Could you share your email address first? Or claim your welcome discount and I will have it ready for you.' };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
      }
      return;
    }

    const lastAssistant = state.messages.filter((m) => m.role === 'assistant').slice(-1)[0];
    if (looksLikeZipCode(message) && lastAssistant && isShippingQuery(lastAssistant.content)) {
      const typingMsg = { role: 'assistant', content: 'Checking shipping rates…' };
      state.messages.push(typingMsg);
      renderMessage(feed, typingMsg);

      const estimate = await fetchShippingEstimate(root.dataset.workerUrl, message.trim());
      state.messages.pop();
      feed.lastElementChild?.remove();

      if (estimate && estimate.formatted) {
        const shippingMsg = {
          role: 'user',
          content: `Shipping estimate results: ${estimate.formatted}`,
        };
        state.messages.push(shippingMsg);
        context.liveProducts = [];
        const payload = await requestEzraReply({
          messages: state.messages,
          context,
          workerUrl: root.dataset.workerUrl,
        });
        const reply = {
          role: 'assistant',
          content: payload.reply || estimate.formatted,
        };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
      } else {
        const reply = { role: 'assistant', content: 'I was not able to get shipping rates for that zip code right now. Standard US shipping is free on all orders, and delivery typically takes 4-8 business days after processing.' };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
      }
      return;
    }

    if (isSaveProductTrigger(message)) {
      if (context.pageType === 'product' && context.productHandle) {
        const added = addToWishlist({
          handle: context.productHandle,
          title: context.productTitle || '',
          price: context.productPrice || '',
        });
        const replyContent = added
          ? `Saved the ${context.productTitle || 'product'} to your wishlist. You can say "show my wishlist" anytime to see what you have saved.`
          : `The ${context.productTitle || 'product'} is already on your wishlist.`;
        const reply = { role: 'assistant', content: replyContent };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
      } else {
        const reply = { role: 'assistant', content: 'Navigate to a product page first, then tell me to save it and I will add it to your wishlist.' };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
      }
      return;
    }

    if (isWishlistTrigger(message)) {
      const list = getWishlist();
      if (list.length === 0) {
        const reply = { role: 'assistant', content: 'Your wishlist is empty. When you are on a product page, just say "save this" and I will remember it for you.' };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
      } else {
        const reply = {
          role: 'assistant',
          content: `You have ${list.length} item${list.length > 1 ? 's' : ''} saved. Here they are:`,
        };
        state.messages.push(reply);
        renderMessage(feed, reply);
        saveConversation(state.messages);
        renderProductCards(feed, list.map((p) => ({
          title: p.title,
          handle: p.handle,
          price: p.price || null,
          url: p.url || `/products/${p.handle}`,
        })));
      }
      return;
    }

    if (isStyleQuizTrigger(message)) {
      const quizIntro = {
        role: 'assistant',
        content: 'I would love to help you find the perfect piece. Let me ask a few quick questions.',
      };
      state.messages.push(quizIntro);
      renderMessage(feed, quizIntro);
      saveConversation(state.messages);
      setStatus(root, 'Take the style quiz below.');

      renderQuizStep(feed, 0, [], async (answers) => {
        const searchQuery = buildQuizSearchQuery(answers);
        const quizSummary = `Style quiz results: looking for ${answers[1] || 'apparel'}, preference ${answers[2] || 'any'}, intent ${answers[0] || 'self'}`;
        const quizMsg = { role: 'user', content: quizSummary };
        state.messages.push(quizMsg);
        saveConversation(state.messages);

        setStatus(root, 'Ezra is finding your recommendations...');
        try {
          const liveProducts = await fetchLiveProducts(searchQuery, context);
          const payload = await requestEzraReply(root, state, { ...context, liveProducts, customerName: getCustomerName() });
          const recMsg = {
            role: 'assistant',
            content: payload.reply || 'Based on your answers, here are some picks I think you will love.',
          };
          state.messages.push(recMsg);
          renderMessage(feed, recMsg);
          saveConversation(state.messages);

          if (Array.isArray(payload.liveProducts) && payload.liveProducts.length > 0) {
            renderProductCards(feed, payload.liveProducts);
          } else if (liveProducts.length > 0) {
            renderProductCards(feed, liveProducts);
          }
          setStatus(root, 'Ezra is ready.');
        } catch (error) {
          console.error('Ezra quiz recommendation error', error);
          setStatus(root, 'Something went wrong. Try asking Ezra directly.');
        }
      });
      return;
    }

    try {
      const payload = await requestEzraReply(root, state, { ...context, customerName: getCustomerName() });
      const assistantMessage = {
        role: 'assistant',
        content: payload.reply || 'I am sorry, but I do not have a response for that yet.',
      };
      state.messages.push(assistantMessage);
      renderMessage(feed, assistantMessage);
      saveConversation(state.messages);

      if (Array.isArray(payload.liveProducts) && payload.liveProducts.length > 0) {
        renderProductCards(feed, payload.liveProducts);
      }

      toggleLeadCapture(root, Boolean(payload.escalationSuggested), message, 'support');
      setStatus(root, payload.escalationSuggested ? 'A human follow-up may be best here.' : 'Ezra is ready.');
    } catch (error) {
      console.error('Ezra chat error', error);
      setStatus(root, 'Ezra hit an issue. Please try again in a moment.');
    }
  });

  leadForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    setStatus(root, 'Sending your note...');

    try {
      await submitLeadCapture(root);
      toggleLeadCapture(root, false);
    } catch (error) {
      console.error('Ezra lead capture error', error);
      setStatus(root, 'We could not send that note yet. Please email us directly.');
    }
  });

  initProactiveNudge(root, context);
}

const STYLE_QUIZ_STEPS = [
  {
    question: 'What are you looking for today?',
    options: [
      { label: 'Something for myself', value: 'self' },
      { label: 'A gift for someone', value: 'gift' },
      { label: 'Just browsing', value: 'browse' },
    ],
  },
  {
    question: 'What type of apparel speaks to you?',
    options: [
      { label: 'T-shirts', value: 'shirt' },
      { label: 'Hoodies', value: 'hoodie' },
      { label: 'Sweatshirts', value: 'sweatshirt' },
      { label: 'Tank tops', value: 'tank' },
      { label: 'Accessories', value: 'hat' },
    ],
  },
  {
    question: 'What kind of fit do you prefer?',
    options: [
      { label: 'Relaxed & cozy', value: 'relaxed' },
      { label: 'True to size', value: 'true' },
      { label: 'Not sure yet', value: 'unsure' },
    ],
  },
];

function isStyleQuizTrigger(message) {
  return /\b(style quiz|help me choose|what should i get|recommend something|not sure what to get|quiz)\b/i.test(
    `${message || ''}`
  );
}

function buildQuizSearchQuery(answers) {
  const typeMap = { shirt: 'shirt', hoodie: 'hoodie', sweatshirt: 'sweatshirt', tank: 'tank', hat: 'hat' };
  return typeMap[answers[1]] || 'shirt';
}

function renderQuizStep(container, stepIndex, answers, onComplete) {
  if (stepIndex >= STYLE_QUIZ_STEPS.length) {
    onComplete(answers);
    return;
  }

  const step = STYLE_QUIZ_STEPS[stepIndex];
  const quizDiv = document.createElement('div');
  quizDiv.className = 'ezra-chat__quiz';

  const q = document.createElement('p');
  q.className = 'ezra-chat__quiz-question';
  q.textContent = step.question;
  quizDiv.appendChild(q);

  const optionsDiv = document.createElement('div');
  optionsDiv.className = 'ezra-chat__quiz-options';

  for (const option of step.options) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ezra-chat__quiz-option';
    btn.textContent = option.label;
    btn.addEventListener('click', () => {
      for (const b of optionsDiv.querySelectorAll('.ezra-chat__quiz-option')) {
        b.disabled = true;
      }
      btn.classList.add('ezra-chat__quiz-option--selected');
      answers.push(option.value);
      setTimeout(() => {
        renderQuizStep(container, stepIndex + 1, answers, onComplete);
        container.scrollTop = container.scrollHeight;
      }, 300);
    });
    optionsDiv.appendChild(btn);
  }

  quizDiv.appendChild(optionsDiv);
  container.appendChild(quizDiv);
  container.scrollTop = container.scrollHeight;
}

function buildCartNudgeMessage(context) {
  const items = context.cartItems;
  if (!Array.isArray(items) || items.length === 0) {
    return 'Have any questions before checkout?';
  }
  const firstName = (items[0].product_title || items[0].title || '').split(/\s[-–—]/)[0].trim();
  if (firstName && items.length === 1) {
    return `Great pick! Need help with sizing for the ${firstName}?`;
  }
  return `You have ${items.length} items ready — any questions before checkout?`;
}

const PROACTIVE_PROMPTS = {
  product: { delay: 20000, message: 'Need help picking a size or color?' },
  cart: { delay: 10000, message: null },
  collection: { delay: 25000, message: 'Looking for something specific? I can help.' },
};

const NUDGE_SHOWN_PREFIX = 'ezra_nudge_';

function hasNudgeShown(pageType) {
  try {
    return sessionStorage.getItem(`${NUDGE_SHOWN_PREFIX}${pageType}`) === '1';
  } catch { return false; }
}

function markNudgeShown(pageType) {
  try {
    sessionStorage.setItem(`${NUDGE_SHOWN_PREFIX}${pageType}`, '1');
  } catch { /* private browsing */ }
}

function initProactiveNudge(root, context) {
  const config = PROACTIVE_PROMPTS[context.pageType];
  if (!config) return;
  if (hasNudgeShown(context.pageType)) return;

  const nudgeMessage = config.message || (context.pageType === 'cart' ? buildCartNudgeMessage(context) : null);
  if (!nudgeMessage) return;

  const panel = root.querySelector('[data-ezra-panel]');
  const launcher = root.querySelector('[data-ezra-launcher]');
  if (!panel || !launcher) return;

  const timer = setTimeout(() => {
    if (!panel.hidden && panel.style.display !== 'none') return;

    markNudgeShown(context.pageType);

    const nudge = document.createElement('div');
    nudge.className = 'ezra-chat__nudge';
    nudge.innerHTML = `
      <p class="ezra-chat__nudge-text">${nudgeMessage}</p>
      <button class="ezra-chat__nudge-dismiss" type="button" aria-label="Dismiss">&times;</button>
    `;

    root.insertBefore(nudge, launcher);

    const dismiss = () => {
      nudge.classList.add('ezra-chat__nudge--out');
      setTimeout(() => nudge.remove(), 300);
    };

    nudge.querySelector('.ezra-chat__nudge-dismiss').addEventListener('click', (e) => {
      e.stopPropagation();
      dismiss();
    });

    nudge.addEventListener('click', () => {
      dismiss();
      panel.hidden = false;
      launcher.hidden = true;
      panel.style.display = '';
      launcher.style.display = 'none';
    });

    setTimeout(dismiss, 8000);
  }, config.delay);

  launcher.addEventListener('click', () => clearTimeout(timer), { once: true });
}

function boot() {
  document.querySelectorAll('[data-ezra-root]').forEach((root) => {
    try {
      initEzra(root);
    } catch (err) {
      console.error('Ezra init error', err);
    }
  });
  window.__ezraReady = true;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
